import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listSubmissions } from '@/services/submissionsApi';
import { listProblems } from '@/services/problemsApi';
import type { SubmissionStatus } from '@/services/types';
import { queryKeys } from '@/lib/queryKeys';
import { formatPassRatio } from '@/lib/submissions';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState from '@/components/common/ContentErrorState';
import EmptyState from '@/components/common/EmptyState';
import SectionCard from '@/components/common/SectionCard';
import SubmissionStatusBadge from '@/components/submissions/SubmissionStatusBadge';

const STATUS_FILTERS: Array<{ value: SubmissionStatus | 'ALL'; label: string }> = [
  { value: 'ALL', label: '全部状态' },
  { value: 'QUEUED', label: '排队中' },
  { value: 'PENDING', label: '等待判题' },
  { value: 'RUNNING', label: '判题中' },
  { value: 'ACCEPTED', label: '通过' },
  { value: 'WRONG_ANSWER', label: '答案错误' },
  { value: 'RUNTIME_ERROR', label: '运行错误' },
  { value: 'COMPILE_ERROR', label: '编译错误' },
  { value: 'TIME_LIMIT_EXCEEDED', label: '超时' },
  { value: 'MEMORY_LIMIT_EXCEEDED', label: '内存超限' },
];

export default function SubmissionsPage() {
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | 'ALL'>('ALL');

  const {
    data: submissions,
    isLoading,
    isError,
    error: submissionsError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.submissions.list,
    queryFn: listSubmissions,
    retry: 1,
  });

  const { data: problems } = useQuery({
    queryKey: queryKeys.problems.list({}),
    queryFn: () => listProblems({}),
    retry: 1,
  });

  const problemTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const problem of problems ?? []) {
      map.set(problem.id, problem.title);
    }
    return map;
  }, [problems]);

  const filteredSubmissions = useMemo(() => {
    const text = keyword.trim().toLowerCase();

    return (submissions ?? []).filter((submission) => {
      if (statusFilter !== 'ALL' && submission.status !== statusFilter) {
        return false;
      }

      if (!text) {
        return true;
      }

      const title = problemTitleMap.get(submission.problemId) ?? '';
      return (
        submission.id.toLowerCase().includes(text) ||
        submission.problemId.toLowerCase().includes(text) ||
        title.toLowerCase().includes(text)
      );
    });
  }, [keyword, statusFilter, submissions, problemTitleMap]);

  if (isLoading) {
    return <PageLoading message='提交记录加载中…' />;
  }

  return (
    <div className='max-w-[1080px] mx-auto px-6 py-12'>
      <h1
        className='font-semibold text-apple-near-black mb-2'
        style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
      >
        提交记录
      </h1>
      <p className='text-apple-gray mb-8 text-[0.9375rem]'>查看历史提交、判题状态与结果摘要。</p>

      <SectionCard className='mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
          <input
            className='input'
            placeholder='搜索提交 ID、题目 ID 或题目标题'
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
          <select
            className='input'
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as SubmissionStatus | 'ALL')}
          >
            {STATUS_FILTERS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => {
              setKeyword('');
              setStatusFilter('ALL');
            }}
          >
            重置筛选
          </button>
        </div>
      </SectionCard>

      {isError ? <ContentApiErrorState error={submissionsError} onRetry={() => void refetch()} /> : null}

      {!isError && filteredSubmissions.length === 0 ? (
        <EmptyState
          title='暂无提交记录'
          description='提交代码后，判题结果会显示在这里。'
          action={<Link to='/problems' className='btn btn-primary'>去做题</Link>}
        />
      ) : null}

      <div className='space-y-3 mt-4'>
        {filteredSubmissions.map((submission) => {
          const problemTitle = problemTitleMap.get(submission.problemId) ?? `题目 ${submission.problemId}`;

          return (
            <Link
              key={submission.id}
              to={`/submissions/${submission.id}`}
              className='block bg-white rounded-2xl p-5 shadow-card-sm hover:shadow-card transition-shadow'
            >
              <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3'>
                <div className='space-y-1'>
                  <p className='text-apple-near-black font-medium text-[0.9375rem]'>{problemTitle}</p>
                  <p className='text-apple-gray text-[0.8125rem]'>
                    提交时间：{formatDateTime(submission.createdAt)} · 提交 ID：{submission.id}
                  </p>
                </div>

                <div className='flex items-center gap-3'>
                  <SubmissionStatusBadge status={submission.status} />
                  <span className='text-apple-near-black text-[0.875rem] min-w-[68px] text-right'>
                    {formatPassRatio(submission.result?.passRatio ?? 0)}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('zh-CN', {
    hour12: false,
  });
}
